/**
 * EAA-1736: Audio NodeView for TipTap.
 *
 * Audio previously had no NodeView (it rendered straight from renderHTML), so it
 * could not participate in the shared media layout (wrap / behind / front) or the
 * right-click context menu. This view wraps the <audio> element in MediaLayoutFrame
 * and resolves storage-path srcs for playback in the editor (matching images).
 */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { Loader2, Pause, Play } from 'lucide-react';
import {
    resolveMediaUrlForViewer,
    shouldResolveMediaUrlForViewer,
} from '@ai-content-hub/content-core';
import { cn } from '../../../lib/utils';
import { MediaLayoutFrame } from './MediaLayoutFrame';
import { Button } from '../../ui/button';
import type { AudioPlayerStyle } from '../../../lib/tiptap-audio';

export const AudioView: React.FC<NodeViewProps> = (props) => {
    const { node, updateAttributes, deleteNode, selected, view, getPos } = props;

    // EAA-1736: native <audio controls> chrome (the seek/volume sliders) is a
    // browser-internal widget, and interacting with it can leave ProseMirror's
    // own click-to-select handling never firing, so editor.isActive('audio')
    // stays false and MediaBubbleMenu (the layout toolbar shown for a selected
    // image/audio/embed) never appears — unlike a plain <img>, which has no such
    // native widget in the way. Force the ProseMirror NodeSelection explicitly
    // instead of relying on the native click reaching ProseMirror's handler.
    const handleSelect = useCallback(() => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        const { state } = view;
        if (state.selection instanceof NodeSelection && state.selection.from === pos) return;
        view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)));
    }, [view, getPos]);

    const src = (node.attrs as { src?: string }).src ?? '';
    const title = (node.attrs as { title?: string | null }).title ?? undefined;
    const playerStyle: AudioPlayerStyle =
        (node.attrs as { style?: AudioPlayerStyle }).style === 'minimal' ? 'minimal' : 'full';

    const [displaySrc, setDisplaySrc] = useState<string>(() =>
        src && !shouldResolveMediaUrlForViewer(src) ? src : ''
    );
    const [resolving, setResolving] = useState(() =>
        Boolean(src && shouldResolveMediaUrlForViewer(src))
    );

    useEffect(() => {
        if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
            setDisplaySrc(src);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(src)) {
            setDisplaySrc(src);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setDisplaySrc('');
        void (async () => {
            try {
                const resolved = await resolveMediaUrlForViewer(src);
                if (!cancelled) {
                    setDisplaySrc(resolved || src);
                }
            } catch {
                if (!cancelled) {
                    setDisplaySrc(src);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [src]);

    // EAA-1736: minimal style plays through a hidden <audio> element controlled
    // imperatively via a ref, driven by a plain icon button instead of the
    // native controls bar.
    const minimalAudioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handleToggleMinimalPlay = useCallback(() => {
        handleSelect();
        const el = minimalAudioRef.current;
        if (!el) return;
        if (el.paused) {
            void el.play();
        } else {
            el.pause();
        }
    }, [handleSelect]);

    if (!src) return null;

    const audioSrc = displaySrc || (resolving ? '' : src);
    if (playerStyle === 'minimal') {
        return (
            <MediaLayoutFrame
                node={node}
                updateAttributes={updateAttributes}
                deleteNode={deleteNode}
                selected={selected}
                className={cn('tiptap-audio-wrapper', selected && 'tiptap-audio--selected')}
            >
                <span className="tiptap-audio-minimal inline-flex items-center gap-2 align-middle">
                    {!resolving && (
                        <audio
                            ref={minimalAudioRef}
                            src={audioSrc}
                            title={title}
                            preload="metadata"
                            className="tiptap-audio tiptap-audio--minimal-el hidden"
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                        />
                    )}
                    <Button
                        type="button"
                        variant="default"
                        size="icon"
                        className="tiptap-audio-minimal-btn"
                        disabled={resolving}
                        onMouseDown={handleSelect}
                        onFocus={handleSelect}
                        onClick={handleToggleMinimalPlay}
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                        {resolving ? (
                            <Loader2 className="animate-spin" />
                        ) : isPlaying ? (
                            <Pause />
                        ) : (
                            <Play />
                        )}
                    </Button>
                </span>
            </MediaLayoutFrame>
        );
    }

    return (
        <MediaLayoutFrame
            node={node}
            updateAttributes={updateAttributes}
            deleteNode={deleteNode}
            selected={selected}
            className={cn('tiptap-audio-wrapper', selected && 'tiptap-audio--selected')}
        >
            {/* EAA-1736: <audio> has no intrinsic width, so `width:100%` inside a
                shrink-to-fit inline-block collapses the player to 0 and it becomes
                invisible in the editor (it still renders in the player, which uses a
                separate path). Pin a definite width via inline style so it cannot
                collapse regardless of stylesheet timing. */}
            <div
                className="tiptap-audio-inner relative inline-block"
                style={{ width: 340, maxWidth: '100%' }}
                onMouseDown={handleSelect}
                onClick={handleSelect}
            >
                {resolving ? (
                    <div className="tiptap-audio-loading flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                ) : (
                    <audio
                        src={audioSrc}
                        title={title}
                        controls
                        preload="metadata"
                        className="tiptap-audio"
                        style={{ width: '100%', display: 'block' }}
                        // EAA-1736: `focus` is the one signal confirmed to fire when a
                        // user interacts with the native controls (the browser's own
                        // audio:focus outline proves it) — unlike mousedown/click,
                        // which the native seek/volume widgets may swallow before
                        // ProseMirror's own selection handling sees them. Bind here as
                        // the reliable path to force the NodeSelection.
                        onFocus={handleSelect}
                    />
                )}
            </div>
        </MediaLayoutFrame>
    );
};

AudioView.displayName = 'AudioView';
